<template>
  <section class="page-card" aria-label="pagination">
    <div class="pager-row">
      <button
        class="nav-btn"
        type="button"
        :disabled="currentPage <= 1"
        @click="handlePrev"
        aria-label="Previous page"
      >
        <img :src="laIcon" alt="Previous" class="nav-icon" />
      </button>

      <button
        v-for="page in displayPages"
        :key="page"
        class="page-btn"
        :class="{ active: page === currentPage }"
        type="button"
        @click="handlePageChange(page)"
      >
        {{ page }}
      </button>

      <button
        class="nav-btn"
        type="button"
        :disabled="currentPage >= totalPages"
        @click="handleNext"
        aria-label="Next page"
      >
        <img :src="raIcon" alt="Next" class="nav-icon" />
      </button>

      <span class="goto-label">Go to</span>
      <input
        class="goto-input"
        type="number"
        :min="1"
        :max="totalPages"
        :value="gotoPage"
        @input="handleGotoInput"
        @keyup.enter="handleGoToPage"
      />
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import laIcon from '@/assets/la.png'
import raIcon from '@/assets/ra.png'

const props = defineProps<{
  currentPage: number
  totalPages: number
}>()

const emit = defineEmits<{
  'page-change': [page: number]
}>()

const gotoPage = ref(props.currentPage)

watch(
  () => props.currentPage,
  (newVal) => {
    gotoPage.value = newVal
  },
)

const displayPages = computed(() => {
  if (props.totalPages <= 1) {
    return [1]
  }

  const pages = []
  const maxVisible = 5
  let start = Math.max(1, props.currentPage - 2)
  const end = Math.min(props.totalPages, start + maxVisible - 1)

  if (end - start < maxVisible - 1) {
    start = Math.max(1, end - maxVisible + 1)
  }

  for (let i = start; i <= end; i++) {
    pages.push(i)
  }
  return pages
})

function handlePrev(): void {
  if (props.currentPage > 1) {
    emit('page-change', props.currentPage - 1)
  }
}

function handleNext(): void {
  if (props.currentPage < props.totalPages) {
    emit('page-change', props.currentPage + 1)
  }
}

function handlePageChange(page: number): void {
  emit('page-change', page)
}

function handleGotoInput(event: Event): void {
  const target = event.target as HTMLInputElement
  gotoPage.value = parseInt(target.value) || 1
}

function handleGoToPage(): void {
  if (gotoPage.value >= 1 && gotoPage.value <= props.totalPages) {
    emit('page-change', gotoPage.value)
  } else {
    gotoPage.value = props.currentPage
  }
}
</script>

<style scoped>
.page-card {
  width: fit-content;
  padding: 4px 0;
}

.pager-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.nav-btn {
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 0;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  padding: 0;
}

.nav-btn:hover:not(:disabled) {
  opacity: 0.7;
}

.nav-btn:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.nav-icon {
  width: 16px;
  height: 16px;
  object-fit: contain;
  flex-shrink: 0;
}

.page-btn {
  min-width: 40px;
  height: 40px;
  padding: 0 8px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: #000000;
  font-size: 14px;
  font-weight: 700;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  line-height: 1;
  cursor: pointer;
  transition: all 0.2s;
}

.page-btn.active {
  border: none;
  background: #000;
  color: #fff;
  border-radius: 8px;
}

.page-btn:not(.active):hover {
  opacity: 0.7;
}

.goto-label {
  margin-left: 8px;
  color: #000000;
  font-size: 14px;
  font-weight: 700;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
}

.goto-input {
  width: 80px;
  height: 40px;
  padding: 0 8px;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  color: #000000;
  font-size: 14px;
  font-weight: 700;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  text-align: center;
  outline: none;
  background: #fff;
  -webkit-appearance: textfield;
  -moz-appearance: textfield;
  appearance: textfield;
}
.goto-input::-webkit-outer-spin-button,
.goto-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
.goto-input:focus {
  border-color: #666666;
}

@media (max-width: 900px) {
  .nav-btn,
  .page-btn {
    width: 28px;
    height: 28px;
  }

  .goto-label {
    font-size: 12px;
  }

  .goto-input {
    width: 50px;
    height: 28px;
    font-size: 12px;
  }
}
</style>
